import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Alert, IconButton, Stack, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import RequestFilter from './requestFilter';
import RequestTable from './requestTable';
import ApiService from '../../api/api_service';


const MasterRequest = ({
    geometries = [],
    loadingGeometries = false,
    errorGeometries = null,
    onViewOnMap,
    onReplicateRequest
}) => {
    const [selectedFilterId, setSelectedFilterId] = useState('all');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [requests, setRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(true);
    const [errorRequests, setErrorRequests] = useState(null);

    const fetchRequests = useCallback(async () => {
        setLoadingRequests(true);
        setErrorRequests(null);

        const formattedStartDate = startDate ? startDate.startOf('day').toISOString() : null;
        const formattedEndDate = endDate ? endDate.endOf('day').toISOString() : null;
        const geometryIdToFetch = selectedFilterId === 'all' ? null : selectedFilterId;

        console.log(`Fetching requests for geometry ID: ${geometryIdToFetch}, Start: ${formattedStartDate}, End: ${formattedEndDate}`);

        try {
            const data = await ApiService.fetchUserRequests(
                geometryIdToFetch,
                formattedStartDate,
                formattedEndDate
            );
            setRequests(Array.isArray(data) ? data : []);
        } catch (error) {
            setErrorRequests(error.message || "Failed to fetch requests.");
            setRequests([]);
        } finally {
            setLoadingRequests(false);
        }
    }, [selectedFilterId, startDate, endDate]);

    useEffect(() => {
        fetchRequests();
        // Add startDate and endDate to the dependency array
    }, [selectedFilterId, startDate, endDate, fetchRequests]);

    const handleDeleteRequest = useCallback(async (request) => {
        const id = request && (request.id || request.request_id || request._id || request.id_str);
        if (!id) return;
        if (!window.confirm('Delete this request on the server? This cannot be undone.')) return;

        try {
            await ApiService.deleteRequest(id);
            setRequests(prev => prev.filter(r => String(r.id || r.request_id || r._id || r.id_str) !== String(id)));
        } catch (err) {
            console.error('Delete failed', err);
            window.alert('Delete failed: ' + (err.message || 'unknown'));
        }
    }, []);

    // Handler for geometry filter change
    const handleGeomFilterChange = useCallback((geometryId) => {
        setSelectedFilterId(geometryId);
    }, []);

    // Handler for date filter changes
    const handleDateFilterChange = useCallback((type, date) => {
        if (type === 'start') {
            setStartDate(date);
        } else if (type === 'end') {
            setEndDate(date);
        }
    }, []);

    return (
        <Box sx={{ padding: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box mb={2}>
                <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="flex-start"
                >
                    <Tooltip title="Refresh requests">
                        <span>
                            <IconButton
                                color="primary"
                                onClick={fetchRequests}
                                disabled={loadingRequests || loadingGeometries}
                                aria-label="refresh requests"
                                sx={{
                                    boxShadow: 3,
                                    bgcolor: 'background.paper',
                                    transition: 'background-color 0.3s',
                                    '&:hover': {
                                        bgcolor: 'primary.light',
                                    },
                                }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <RequestFilter
                        geometries={geometries}
                        selectedGeometryId={selectedFilterId}
                        onGeometryChange={handleGeomFilterChange}
                        loading={loadingGeometries}
                        disabled={!!errorGeometries || loadingGeometries}
                        startDate={startDate}
                        endDate={endDate}
                        onDateChange={handleDateFilterChange}
                    />
                </Stack>
                {errorGeometries && <Alert severity="error" sx={{ mt: 1 }}>{errorGeometries}</Alert>}
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <RequestTable
                    requests={requests}
                    loading={loadingRequests}
                    error={errorRequests}
                    onViewOnMap={onViewOnMap}
                    onDelete={handleDeleteRequest}
                    onReplicate={onReplicateRequest}
                />
            </Box>
        </Box>
    );
};

export default MasterRequest;
